import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";

// Primary nav items — ALWAYS visible when the desktop nav shows (lg+).
// Kept small (5) so the header fits cleanly at 1024px with the "More" dropdown.
const NAV_PRIMARY = [
  { label: "Rooms", href: "/rooms" },
  { label: "Amenities", href: "/amenities" },
  { label: "Indoor Pool", href: "/indoor-pool" },
  { label: "Meetings & Events", href: "/meetings-events" },
  { label: "Contact", href: "/contact" },
];

// Secondary nav — folded into the "More" dropdown at lg/xl to keep the bar
// single-line and legible at mid-widths. Still surfaced in the mobile drawer.
const NAV_MORE = [
  { label: "Stay With Us", href: "/stay" },
  { label: "Explore Local", href: "/explore" },
  { label: "Journal", href: "/blog" },
  { label: "FAQ", href: "/faq" },
];

// Full list used by the mobile drawer.
const NAV_ALL = [...NAV_PRIMARY.slice(0, 4), ...NAV_MORE, NAV_PRIMARY[4]];

export const BOOKING_URL =
  "https://reservation.asiwebres.com/v2/RoomAvailability.aspx?id=a8021e135c8c4011878c9f3f0e4d9ffd";
export const PHONE_TEL = "+16414377272";
export const PHONE_DISPLAY = "(641) 437-7272";

function isActive(pathname, href) {
  return pathname === href || pathname.startsWith(href + "/");
}

// --------------------------------------------------------------------------
// "More" dropdown — keyboard + click-outside + Escape support, aria-expanded.
// --------------------------------------------------------------------------
function MoreMenu({ pathname }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);

  const anyChildActive = NAV_MORE.some((n) => isActive(pathname, n.href));

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Close on route change.
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="header-more-menu"
        data-testid="nav-more-toggle"
        className={`inline-flex items-center gap-1 whitespace-nowrap transition-colors duration-200 outline-none focus-visible:ring-2 focus-visible:ring-brass/60 rounded-sm ${
          anyChildActive || open
            ? "text-cream font-medium"
            : "text-cream/80 hover:text-cream"
        }`}
      >
        More
        <ChevronDown
          size={14}
          strokeWidth={2}
          className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div
          id="header-more-menu"
          role="menu"
          data-testid="nav-more-menu"
          className="absolute right-0 top-full mt-3 min-w-[13rem] bg-forest-900 border border-forest-700/60 shadow-2xl py-2 animate-fade-up"
        >
          {NAV_MORE.map((n) => (
            <Link
              key={n.href}
              to={n.href}
              role="menuitem"
              onClick={() => setOpen(false)}
              data-testid={`nav-more-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`block px-4 py-2 text-sm whitespace-nowrap transition-colors outline-none focus-visible:bg-forest-800 focus-visible:text-cream ${
                isActive(pathname, n.href)
                  ? "text-cream font-medium"
                  : "text-cream/85 hover:text-cream hover:bg-forest-800"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const isHome = pathname === "/";
  const solid = scrolled || !isHome;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-500 ${
        solid
          ? "bg-forest/95 backdrop-blur-md border-b border-forest-700/40"
          : "bg-forest/40 backdrop-blur-md"
      }`}
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 bg-cream text-forest px-3 py-1 rounded"
      >
        Skip to content
      </a>
      {/* One flex row; every child vertically centered; no wrap; no overflow. */}
      <div className="container-app flex items-center h-16 md:h-20 text-cream gap-4 lg:gap-6 xl:gap-8">
        {/* Logo — fixed width, doesn't shrink */}
        <Link
          to="/"
          className="flex items-center gap-3 group shrink-0 mr-1 lg:mr-3"
          data-testid="brand-home-link"
          aria-label="Westbridge Inn & Suites — home"
        >
          <picture>
            <img
              src="/brand/logo-light.png"
              alt="Westbridge Inn & Suites"
              width="200"
              height="48"
              className="h-9 md:h-11 w-auto"
              data-testid="brand-logo"
            />
          </picture>
        </Link>

        {/* Desktop nav — visible lg+. Every link is nowrap; consistent flex gap. */}
        <nav
          className="hidden lg:flex items-center gap-5 xl:gap-7 text-[0.94rem] leading-none flex-1 min-w-0"
          aria-label="Primary"
        >
          {NAV_PRIMARY.map((n) => (
            <Link
              key={n.href}
              to={n.href}
              className={`whitespace-nowrap transition-colors duration-200 outline-none focus-visible:ring-2 focus-visible:ring-brass/60 rounded-sm ${
                isActive(pathname, n.href)
                  ? "text-cream font-medium"
                  : "text-cream/80 hover:text-cream"
              }`}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {n.label}
            </Link>
          ))}
          {/* Push More + CTAs to the right */}
          <div className="ml-auto flex items-center gap-5 xl:gap-7">
            <MoreMenu pathname={pathname} />
          </div>
        </nav>

        {/* Right cluster: phone (md+), Book Now (always), hamburger (below lg) */}
        <div className="flex items-center gap-2 md:gap-3 shrink-0 ml-auto lg:ml-0">
          <a
            href={`tel:${PHONE_TEL}`}
            className="hidden md:inline-flex items-center gap-1.5 text-sm text-cream/85 hover:text-cream whitespace-nowrap tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-brass/60 rounded-sm"
            data-testid="header-call-link"
          >
            <Phone size={16} strokeWidth={1.8} />
            <span>{PHONE_DISPLAY}</span>
          </a>
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary text-sm py-2 px-4 md:py-2.5 md:px-5 whitespace-nowrap"
            data-testid="header-book-now"
          >
            Book Now
          </a>
          <button
            className="lg:hidden text-cream p-2 -mr-2 outline-none focus-visible:ring-2 focus-visible:ring-brass/60 rounded-sm"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav-drawer"
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((o) => !o)}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer — full NAV_ALL for parity with desktop */}
      {mobileOpen && (
        <div
          id="mobile-nav-drawer"
          className="lg:hidden bg-forest-900 border-t border-forest-700/60 text-cream animate-fade-up"
          data-testid="mobile-menu-drawer"
        >
          <div className="container-app py-6 flex flex-col gap-4">
            {NAV_ALL.map((n) => (
              <Link
                key={n.href}
                to={n.href}
                className="text-lg font-display tracking-tight"
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setMobileOpen(false)}
              >
                {n.label}
              </Link>
            ))}
            <a
              href={`tel:${PHONE_TEL}`}
              className="text-lg font-display tracking-tight text-brass whitespace-nowrap tabular-nums"
              data-testid="mobile-call-link"
            >
              Call {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
